"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X, Home, User, Settings, Mail, Info } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const defaultMenuItems = [
  { id: 1, title: "Accueil", url: "#top", icon: <Home className="h-5 w-5" /> },
  { id: 3, title: "Services", url: "#services", icon: <Settings className="h-5 w-5" /> },
  { id: 5, title: "Travaux", url: "#projects", icon: <Info className="h-5 w-5" /> },
  { id: 2, title: "pourquoi moi", url: "#why-us", icon: <User className="h-5 w-5" /> },
  { id: 4, title: "Contact", url: "#contact", icon: <Mail className="h-5 w-5" /> },
];

export function ScrollNavigationMenu({ menuItems = defaultMenuItems, className = "" }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const { scrollY } = useScroll();
  const navigate = useNavigate();
  const location = useLocation();

  useMotionValueEvent(scrollY, "change", (latest) => {
    setIsScrolled(latest > 100);
  });

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  const menuVariants = {
    closed: {
      opacity: 0,
      scale: 0.8,
      y: -50,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        when: "afterChildren",
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
    open: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    closed: { y: 20, opacity: 0, scale: 0.8 },
    open: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: { type: "spring", stiffness: 400, damping: 25 },
    },
  };

  const hamburgerVariants = {
    normal: { rotate: 0, scale: 1 },
    scrolled: { rotate: 360, scale: 1.1 },
  };

  const handleAnchorScroll = (anchor) => {
    const id = anchor.replace("#", "");
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }

    const el = document.getElementById(id);
    if (!el) return;
    const nav = document.getElementById("site-nav");
    const navHeight = nav ? nav.getBoundingClientRect().height : 0;
    const y = el.getBoundingClientRect().top + window.scrollY - navHeight - 12;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  };

  const renderMenuItem = (item, isMobile = false) => {
    if (item.url.startsWith("#")) {
      return (
        <button
          type="button"
          onClick={() => {
            handleAnchorScroll(item.url);
            if (isMobile) setIsMenuOpen(false);
          }}
          className={
            isMobile
              ? "group flex w-full items-center space-x-4 rounded-xl p-4 text-left transition-colors hover:bg-white/[0.05]"
              : "flex items-center space-x-2 rounded-md px-3 py-2 text-sm font-medium text-zinc-200 transition-colors hover:text-brand"
          }
        >
          {item.icon}
          <span className={isMobile ? "text-lg font-medium text-white group-hover:text-brand" : ""}>{item.title}</span>
        </button>
      );
    }

    return (
      <Link
        to={item.url}
        onClick={() => isMobile && setIsMenuOpen(false)}
        className={
          isMobile
            ? "group flex items-center space-x-4 rounded-xl p-4 transition-colors hover:bg-white/[0.05]"
            : "flex items-center space-x-2 rounded-md px-3 py-2 text-sm font-medium text-zinc-200 transition-colors hover:text-brand"
        }
      >
        {item.icon}
        <span className={isMobile ? "text-lg font-medium text-white group-hover:text-brand" : ""}>{item.title}</span>
      </Link>
    );
  };

  return (
    <>
      <motion.nav
        id="site-nav"
        initial={{ y: 0, opacity: 1 }}
        animate={{ y: isScrolled ? -100 : 0, opacity: isScrolled ? 0 : 1 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className={`fixed left-0 right-0 top-0 z-50 border-b border-white/[0.08] bg-surface/80 backdrop-blur-md ${className}`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <motion.div className="flex-shrink-0" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link to="/" className="font-display text-2xl font-bold text-white">
                aimrane<span className="text-brand">.</span>
              </Link>
            </motion.div>

            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {menuItems.map((item) => (
                  <motion.div
                    key={item.id}
                    className="relative"
                    onMouseEnter={() => setHoveredItem(item.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {renderMenuItem(item)}
                    {hoveredItem === item.id && (
                      <motion.div
                        layoutId="navbar-hover"
                        className="absolute inset-0 -z-10 rounded-md bg-white/[0.06]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      />
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="md:hidden">
              <motion.button
                type="button"
                onClick={toggleMenu}
                className="rounded-md p-2 text-zinc-100 hover:text-brand focus:outline-none"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Menu className="h-6 w-6" />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: isScrolled ? 1 : 0, opacity: isScrolled ? 1 : 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed right-6 top-6 z-50"
      >
        <motion.button
          type="button"
          onClick={toggleMenu}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-brand text-black shadow-lg"
          variants={hamburgerVariants}
          animate={isScrolled ? "scrolled" : "normal"}
          whileHover={{ scale: 1.1, rotate: 180 }}
          whileTap={{ scale: 0.9 }}
        >
          <Menu className="h-6 w-6" />
        </motion.button>
      </motion.div>

      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/55 backdrop-blur-sm"
              onClick={toggleMenu}
            />

            <motion.div
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="relative max-h-[80vh] min-w-[300px] max-w-md overflow-y-auto rounded-3xl border border-white/[0.1] bg-surface-raised p-8 shadow-2xl">
                <motion.button
                  type="button"
                  onClick={toggleMenu}
                  className="absolute right-4 top-4 rounded-full p-2 text-zinc-100 hover:bg-white/[0.06] hover:text-brand"
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X className="h-5 w-5" />
                </motion.button>

                <div className="mt-8 space-y-4">
                  {menuItems.map((item) => (
                    <motion.div key={item.id} variants={itemVariants} whileHover={{ scale: 1.05, x: 10 }} whileTap={{ scale: 0.95 }}>
                      {renderMenuItem(item, true)}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export function Component(props) {
  return <ScrollNavigationMenu {...props} />;
}
